import { SAFE_MESSAGES } from '../../../shared/src/index';
import { canonicalPublicAddress, isIpLiteral, isPublicIp, stripIpBrackets } from './addresses';

const EXACT_BLOCKED_HOSTS = new Set([
  'localhost',
  'localhost.localdomain',
  'ip6-localhost',
  'ip6-loopback',
  'broadcasthost',
  'metadata',
  'metadata.google.internal',
  'metadata.goog',
  'metadata.azure.com',
  'instance-data',
  'instance-data.ec2.internal',
  'kubernetes',
  'kubernetes.default',
  'kubernetes.default.svc',
  'kubernetes.default.svc.cluster.local',
  'host.docker.internal',
  'gateway.docker.internal',
]);

const BLOCKED_SUFFIXES = [
  '.localhost',
  '.local',
  '.internal',
  '.localdomain',
  '.intranet',
  '.lan',
  '.home',
  '.home.arpa',
  '.corp',
  '.localnet',
];

const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

export type UrlDecision =
  | { ok: true; url: URL; strippedPath: boolean }
  | { ok: false; kind: 'invalid'; code: string; message: string }
  | { ok: false; kind: 'blocked'; code: 'destination_rejected'; message: string; displayUrl: string };

function invalid(code: string, message: string): UrlDecision {
  return { ok: false, kind: 'invalid', code, message };
}

export function isBlockedHostname(hostname: string): boolean {
  const host = stripIpBrackets(hostname);
  if (!host || EXACT_BLOCKED_HOSTS.has(host)) return true;
  return BLOCKED_SUFFIXES.some((suffix) => host.endsWith(suffix) || host === suffix.slice(1));
}

export function isDnsHostname(hostname: string): boolean {
  if (hostname.length === 0 || hostname.length > 253) return false;
  const labels = hostname.split('.');
  if (labels.length < 2) return false;
  return labels.every((label) => HOST_LABEL.test(label));
}

function formatHost(hostname: string): string {
  return hostname.includes(':') ? `[${hostname}]` : hostname;
}

function cleanUrl(protocol: string, hostname: string, port: string, pathname: string, search: string): URL {
  const url = new URL(`${protocol}//${formatHost(hostname)}${port ? `:${port}` : ''}/`);
  url.pathname = pathname || '/';
  url.search = search;
  url.hash = '';
  return url;
}

function decideParsed(url: URL, allowPath: boolean): UrlDecision {
  if (url.username || url.password) {
    return invalid('credentials', SAFE_MESSAGES.credentials);
  }

  const protocol = url.protocol.toLowerCase();
  if (protocol !== 'http:' && protocol !== 'https:') {
    return invalid('unsupported_scheme', SAFE_MESSAGES.unsupportedScheme);
  }

  if (url.port && url.port !== '80' && url.port !== '443') {
    return invalid('bad_port', SAFE_MESSAGES.badPort);
  }
  if (protocol === 'http:' && url.port === '443') {
    return invalid('bad_port', SAFE_MESSAGES.badPort);
  }
  if (protocol === 'https:' && url.port === '80') {
    return invalid('bad_port', SAFE_MESSAGES.badPort);
  }

  const hostname = stripIpBrackets(url.hostname);
  if (!hostname) return invalid('invalid_url', SAFE_MESSAGES.invalidUrl);

  const strippedPath = url.pathname !== '/' || url.search.length > 0;
  const pathname = allowPath ? url.pathname || '/' : '/';
  const search = allowPath ? url.search : '';

  if (isIpLiteral(hostname)) {
    if (!isPublicIp(hostname) || !canonicalPublicAddress(hostname)) {
      const display = cleanUrl(protocol, hostname, '', '/', '');
      return {
        ok: false,
        kind: 'blocked',
        code: 'destination_rejected',
        message: SAFE_MESSAGES.destinationRejected,
        displayUrl: display.href,
      };
    }
    const canonical = canonicalPublicAddress(hostname);
    if (!canonical) {
      return invalid('invalid_url', SAFE_MESSAGES.invalidUrl);
    }
    return {
      ok: true,
      url: cleanUrl(protocol, canonical.address, '', pathname, search),
      strippedPath: !allowPath && strippedPath,
    };
  }

  if (isBlockedHostname(hostname) || !isDnsHostname(hostname)) {
    const displayHost = isDnsHostname(hostname) || isBlockedHostname(hostname) ? hostname : '';
    const displayUrl = displayHost
      ? cleanUrl(protocol, displayHost, '', '/', '').href
      : `${protocol}//${hostname}/`;
    return {
      ok: false,
      kind: 'blocked',
      code: 'destination_rejected',
      message: SAFE_MESSAGES.destinationRejected,
      displayUrl,
    };
  }

  return {
    ok: true,
    url: cleanUrl(protocol, hostname, '', pathname, search),
    strippedPath: !allowPath && strippedPath,
  };
}

export function parseHttpUrl(input: string, allowPath: boolean): UrlDecision {
  const trimmed = input.trim();
  if (!trimmed || trimmed.length > 2048) return invalid('invalid_url', SAFE_MESSAGES.invalidUrl);
  if (/[\u0000-\u001F\u007F\\]/.test(trimmed) || /\s/.test(trimmed)) {
    return invalid('invalid_url', SAFE_MESSAGES.invalidUrl);
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return invalid('invalid_url', SAFE_MESSAGES.invalidUrl);
  }

  return decideParsed(url, allowPath);
}

export function normalizeInitialUrl(input: string): UrlDecision {
  return parseHttpUrl(input, false);
}

export function resolveRedirect(current: URL, location: string): UrlDecision {
  const trimmed = location.trim();
  if (!trimmed || trimmed.length > 2048) return invalid('invalid_url', SAFE_MESSAGES.badRedirect);
  if (/[\u0000-\u001F\u007F\\]/.test(trimmed) || /\s/.test(trimmed)) {
    return invalid('invalid_url', SAFE_MESSAGES.badRedirect);
  }

  let url: URL;
  try {
    url = new URL(trimmed, current);
  } catch {
    return invalid('invalid_url', SAFE_MESSAGES.badRedirect);
  }

  const decision = decideParsed(url, true);
  if (!decision.ok) {
    if (decision.kind === 'invalid') {
      return invalid('invalid_url', SAFE_MESSAGES.redirectBlocked);
    }
    return {
      ...decision,
      message: SAFE_MESSAGES.redirectBlocked,
    };
  }
  if (decision.url.href.length > 2048) {
    return invalid('invalid_url', SAFE_MESSAGES.redirectBlocked);
  }
  return decision;
}
